import React, { useState, useEffect } from "react";
import Modal from './Modal'
import { RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { FaHome, FaCheck, FaTimes, FaClock } from "react-icons/fa";
import VacateWorkflowTimeline from "./VacateWorkflowTimeline";
import { API_BASE } from '../config'

function decodeIdTokenEmail() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return (JSON.parse(json).email || "").toString().toLowerCase();
  } catch {
    return null;
  }
}

function decodeIdTokenCommunity() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const data = JSON.parse(json);
    return data.community_id || data.communityId;
  } catch {
    return null;
  }
}

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

function getStatusBadge(status) {
  const badgeConfig = {
    pending: { bg: "bg-yellow-100", text: "text-yellow-800", icon: FaClock, label: "Pending" },
    admin_review: { bg: "bg-purple-100", text: "text-purple-800", icon: FaClock, label: "Admin Review" },
    admin_approved: { bg: "bg-blue-100", text: "text-blue-800", icon: FaClock, label: "Awaiting Your Review" },
    admin_rejected: { bg: "bg-red-100", text: "text-red-800", icon: FaTimes, label: "Admin Rejected" },
    pending_vacate_confirmation: { bg: "bg-orange-100", text: "text-orange-800", icon: FaClock, label: "Pending Admin Confirmation" },
    owner_review: { bg: "bg-purple-100", text: "text-purple-800", icon: FaClock, label: "Awaiting Your Review" },
    owner_approved: { bg: "bg-teal-100", text: "text-teal-800", icon: FaCheck, label: "You Approved" },
    owner_rejected: { bg: "bg-red-100", text: "text-red-800", icon: FaTimes, label: "Rejected" },
    vacate_confirmed: { bg: "bg-green-100", text: "text-green-800", icon: FaCheck, label: "Vacate Confirmed" },
  };

  const config = badgeConfig[status] || badgeConfig.pending;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text} inline-flex items-center gap-1`}>
      <config.icon size={12} />
      {config.label}
    </span>
  );
}

export default function OwnerVacateManagement({ unitNbr, communityId: propCommunityId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const email = decodeIdTokenEmail();
  const communityId = propCommunityId || decodeIdTokenCommunity() || sessionStorage.getItem("community_id");

  const fetchRequests = async () => {
    if (!communityId || !unitNbr || !email) return;
    try {
      setLoading(true);
      const url = `/vacate-requests?communityId=${communityId}&unitNbr=${encodeURIComponent(unitNbr)}&emailId=${encodeURIComponent(email)}`;
      const data = await apiFetch(url);
      const filtered = Array.isArray(data)
        ? data.filter((r) =>
            r.STATUS === "owner_review" ||
            r.STATUS === "admin_approved" ||
            r.STATUS === "pending_vacate_confirmation" ||
            r.STATUS === "owner_approved" ||
            r.STATUS === "owner_rejected" ||
            r.STATUS === "vacate_confirmed"
          )
        : [];
      setRequests(filtered);
    } catch (err) {
      console.error("Error fetching vacate requests:", err);
      setError("Failed to load vacate requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [communityId, unitNbr]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      const payload = {
        COMMUNITY_ID: communityId,
        VACATE_ID: selectedRequest.VACATE_ID,
        ACTION: "owner_approve",
        REVIEWED_BY: email,
        NOTES: reviewNotes,
      };

      await apiFetch("/vacate-requests", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setSuccess("Vacate request approved!");
      setSelectedRequest(null);
      setReviewNotes("");
      fetchRequests();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error approving request:", err);
      setError("Failed to approve request");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !reviewNotes.trim()) {
      setError("Please provide a rejection reason");
      return;
    }
    try {
      setActionLoading(true);
      const payload = {
        COMMUNITY_ID: communityId,
        VACATE_ID: selectedRequest.VACATE_ID,
        ACTION: "owner_reject",
        REVIEWED_BY: email,
        NOTES: reviewNotes,
      };

      await apiFetch("/vacate-requests", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setSuccess("Vacate request rejected.");
      setSelectedRequest(null);
      setReviewNotes("");
      fetchRequests();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error rejecting request:", err);
      setError("Failed to reject request");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <FaHome className="text-2xl text-indigo-600" />
        <h2 className="text-2xl font-bold text-gray-800">Vacate Requests - Owner Review</h2>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg flex items-center gap-2">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Refresh */}
      <div className="mb-4">
        <button
          onClick={fetchRequests}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Requests List */}
      {loading && <div className="text-center py-8 text-gray-500">Loading requests...</div>}

      {!loading && requests.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FaClock className="mx-auto text-3xl mb-2 opacity-50" />
          <p>No pending vacate requests for your units</p>
        </div>
      )}

      {!loading && requests.length > 0 && (
        <div className="space-y-4">
          {requests.map((req) => (
            <div
              key={req.VACATE_ID}
              onClick={() => setSelectedRequest(req)}
              className="border rounded-lg p-4 hover:shadow-md transition cursor-pointer bg-gray-50"
            >
              <div className="flex justify-between items-center mb-3">
                <div>
                  <p className="font-semibold text-gray-800">Unit {req.UNIT_NBR}</p>
                  <p className="text-sm text-gray-500">{req.TENANT_NAME || req.TENANT_EMAIL}</p>
                </div>
                {getStatusBadge(req.STATUS)}
              </div>
              <VacateWorkflowTimeline request={req} />
            </div>
          ))}
        </div>
      )}

      {selectedRequest && (
        <Modal title={`Vacate Request — Unit ${selectedRequest.UNIT_NBR}`} onClose={() => setSelectedRequest(null)}>
          <p className="text-sm text-gray-500 mb-4">{selectedRequest.TENANT_NAME || selectedRequest.TENANT_EMAIL}</p>

          {/* Full Workflow Status */}
          <div className="mb-5 p-4 bg-gray-50 rounded-lg border">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Approval Workflow</p>
            <VacateWorkflowTimeline request={selectedRequest} />
          </div>

          {/* Action form — for requests awaiting owner review */}
          {["admin_approved", "owner_review"].includes(selectedRequest.STATUS) && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Your Review Notes</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Enter your approval or rejection reason..."
                  rows="3"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex gap-2">
                <button onClick={() => setSelectedRequest(null)} className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold">Close</button>
                <button onClick={handleReject} disabled={actionLoading} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50">
                  {actionLoading ? "Processing..." : "Reject"}
                </button>
                <button onClick={handleApprove} disabled={actionLoading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50">
                  {actionLoading ? "Processing..." : "Approve"}
                </button>
              </div>
            </>
          )}

          {selectedRequest.STATUS === "pending_vacate_confirmation" && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
              <p className="text-sm font-semibold text-orange-800">⏳ Awaiting Admin Final Confirmation</p>
              <p className="text-xs text-orange-700 mt-1">
                You have approved this request. The admin will confirm the actual vacate date and complete the process.
              </p>
            </div>
          )}

          {selectedRequest.STATUS === "vacate_confirmed" && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
              <p className="text-sm font-semibold text-green-800">✓ Vacate Confirmed</p>
              <p className="text-xs text-green-700 mt-1">
                The tenant has vacated. Your unit is now registered as vacant and your account has been updated.
              </p>
            </div>
          )}

          {["pending_vacate_confirmation", "owner_rejected", "vacate_confirmed"].includes(selectedRequest.STATUS) && (
            <button onClick={() => setSelectedRequest(null)} className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold">
              Close
            </button>
          )}
        </Modal>
      )}
    </div>
  );
}
